import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Input,
  Select
} from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { DefaultRequest } from 'scripts/api';
import { actionCreators } from 'react/api/actions/modals';
import { getResourceTo } from 'react/utils/utils';
import Modal from 'react/components/Modal';

import { getFormFields } from '../utils/getFormFields';
import { protocolOptions } from '../utils/protocolOptions';
import { FormContainer, ModalFooter } from './styles';

function DataImportModal({ getTableData }) {
  const [loadingTestConn, setLoadingTestConn] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const formFields = getFormFields();
  const dispatch = useDispatch();

  const MODAL_KEY = 'externalStorage';
  const isOpen = useSelector((state) => state.modals.getIn([MODAL_KEY, 'isOpen']));
  const title = useSelector((state) => state.modals.getIn([MODAL_KEY, 'title']));
  const modalData = useSelector((state) => state.modals.getIn([MODAL_KEY, 'data']).toJS());

  const INITIAL_FORM_DATA = {
    ftpHost: '',
    ftpUser: '',
    ftpPass: '',
    ftpPort: '21',
    ftpType: '',
    name: ''
  };

  const [formData, setFormData] = useState({
    ...INITIAL_FORM_DATA
  });

  const clearForm = () => {
    setFormData({
      ...INITIAL_FORM_DATA
    });
    setLoadingSave(false);
  };

  const closeModal = useCallback(() => {
    clearForm();
    dispatch(actionCreators.closeModal({
      modalKey: MODAL_KEY
    }));
  }, []);

  const mountAlertMessage = ({ message, type = 'error', errors = [] }) => {
    const monetMessage = new Monet.Form.Messages.InlineMessage();
    monetMessage.setScope('.logDetailsModal');
    monetMessage.setInfoType(type);

    if (type === 'error') {
      const errorMessages = errors.map((item) => Monet.Form.Messages.mountErrorMessageObject(item));
      monetMessage.setMessage({
        errorMessages
      });
    } else {
      monetMessage.setMessage(message);
    }

    monetMessage.triggerMessage();
  };

  const validateForm = (values) => {
    const errors = Object.entries(values).reduce((acc, current) => {
      const [key, value] = current;

      if (!value) {
        return [...acc, getResourceTo('field.required', formFields[key])];
      }

      return acc;
    }, []);

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm(formData);

    if (errors.length) {
      mountAlertMessage({ type: 'error', errors });
      return;
    }

    setLoadingSave(true);

    if (formData.id) {
      const request = new DefaultRequest({
        method: 'PUT',
        url: `/ExternalStorage/${formData.id}`,
        data: formData,
        success: () => {
          clearForm();
          closeModal();
          getTableData();
        },
        error: () => {
          setLoadingSave(false);
          mountAlertMessage({
            errors: ['ExternalStorage.connectiontest.error.Undefined']
          });
        }
      });
      request.send();
      return;
    }

    const request = new DefaultRequest({
      method: 'POST',
      url: '/ExternalStorage',
      data: formData,
      success: () => {
        closeModal();
        clearForm();
        getTableData();
      },
      error: () => {
        setLoadingSave(false);
        mountAlertMessage({
          errors: ['ExternalStorage.connectiontest.error.Undefined']
        });
      }
    });
    request.send();
  };

  const handleTestConnection = () => {
    const errors = validateForm(formData);

    if (errors.length) {
      mountAlertMessage({ type: 'error', errors });
      return;
    }

    setLoadingTestConn(true);
    const request = new DefaultRequest({
      method: 'POST',
      url: 'ExternalStorage/connectionTest',
      data: formData,
      success: (data) => {
        setLoadingTestConn(false);

        if (data.success) {
          mountAlertMessage({
            message: Monet.I18n.getResourcesByKey('ExternalStorage.connectiontest.Success'),
            type: 'success'
          });

          return;
        }

        const alertTypes = {
          HOST: 'ExternalStorage.connectiontest.error.HostOrPort',
          LOGIN: 'ExternalStorage.connectiontest.error.LoginOrPassword'
        };

        mountAlertMessage({
          errors: [alertTypes[data.type] || 'ExternalStorage.connectiontest.error.Undefined']
        });
      },
      error: () => {
        setLoadingTestConn(false);
        mountAlertMessage({
          errors: ['ExternalStorage.connectiontest.error.Undefined']
        });
      }
    });
    request.send();
  };

  const formFieldProps = (name) => ({
    onChange: (event) => {
      const { value } = event.target || event;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    required: true,
    value: formData[name],
    label: formFields[name],
    name
  });

  useEffect(() => {
    if (modalData.id && !formData.id) {
      setFormData({
        ...modalData
      });
    }
  }, [modalData]);

  return (
    <Modal
      className="logDetailsModal"
      isOpen={isOpen}
      title={title}
      crossAxisBtn={false}
      closeModalFn={closeModal}
    >

      <div className="monetInlineAlerContainer" />
      <FormContainer>
        <Flex flexDirection="column" gap={8}>
          <Flex gap={12}>
            <Input {...formFieldProps('name')} />
            <Select
              {...formFieldProps('ftpType')}
              defaultValue={modalData.ftpType}
              options={
                protocolOptions.map(({ value, description }) => ({
                  label: getResourceTo(description),
                  value
                }))
              }
            />
          </Flex>

          <Flex gap={12}>
            <Input {...formFieldProps('ftpHost')} />
            <Input {...formFieldProps('ftpPort')} />
          </Flex>

          <Input {...formFieldProps('ftpUser')} />
          <Input type="password" {...formFieldProps('ftpPass')} />
        </Flex>
      </FormContainer>
      <ModalFooter justifyContent="space-between" gap={12}>
        <Button
          disabled={loadingSave || loadingTestConn}
          variant="secondary"
          loading={loadingTestConn}
          onClick={handleTestConnection}
        >
          {getResourceTo('kronosconfiguration.button.testeserver')}
        </Button>
        <Flex alignItems="center" gap={12}>
          <Button disabled={loadingSave || loadingTestConn} variant="secondary" onClick={closeModal}>
            {getResourceTo('default.button.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            loading={loadingSave}
            disabled={loadingSave || loadingTestConn}
          >
            {getResourceTo('default.button.save')}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}

DataImportModal.propTypes = {
  getTableData: PropTypes.func.isRequired
};

export { DataImportModal };
